/**
 * CostingPrintTables — rendu imprimable du pack Costing (3 pages, dans l'ordre) :
 *   1. Labour cost      (LCR build-up par collaborateur)
 *   2. Capacity cost   (CCR + ventilation o/w billable / non billable / idle)
 *   3. Cost assignment   (cascade par cost pool)
 *
 * Source unique : getChrYearly(tenant, year) + fetchPersonnelCostCascade.
 * Les calculs (burden, CCR, owc, BCR, allocations) répliquent ChrPage /
 * CostAssignmentPage via les helpers partagés de personnelCostCascade.
 *
 * Réutilisé par CostingPrintDocument (aperçu PDF) et l'export HTML autonome.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getChrYearly } from '../../api/chrYearly';
import {
  fetchPersonnelCostCascade, readCascadeMode, computeBurdenRatesByPool, computeMarkupRatesByPool,
} from '../../api/tdabc/personnelCostCascade';
import { formatNumber } from '../../utils/format';
import PrintReportHeader from '../hr/print/PrintReportHeader';
import '../hr/print/PeoplePrint.css';

const f0 = (v) => formatNumber(v, 0);
const f2 = (v) => formatNumber(v, 2);

// Libellé de la clé d'allocation (mode cascade) affiché à côté du composant.
const KEY_LABEL = { within: 'Pool specific', shared: 'Practical Capacity', billed: 'Billed hours' };
function withKey(label, m) {
  return (
    <>{label} <span style={{ fontSize: '0.82em', fontStyle: 'italic', fontWeight: 400, color: '#9a8c78' }}>· {KEY_LABEL[m] || m}</span></>
  );
}

export default function CostingPrintTables({ activeTenant, year, editedAt, onDataReady, sections = ['labour', 'assignment', 'production', 'pcr'] }) {
  const { t } = useTranslation();
  const tenantId = activeTenant?.id;
  const show = (s) => sections.includes(s);
  const [chr, setChr] = useState(null);
  const [cascade, setCascade] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!tenantId) return;
    Promise.all([
      getChrYearly(tenantId, year).then(r => { if (r?.error) throw new Error(r.error); return r.data || r; }),
      fetchPersonnelCostCascade(tenantId, year),
    ]).then(([c, cas]) => {
      if (!alive) return;
      setChr(c); setCascade(cas);
      if (onDataReady) onDataReady();
    }).catch(() => { if (onDataReady) onDataReady(); });
    return () => { alive = false; };
  }, [tenantId, year]); // eslint-disable-line

  const mode = readCascadeMode(tenantId);
  const burdenByPool = useMemo(() => cascade ? computeBurdenRatesByPool(cascade, mode) : {}, [cascade]); // eslint-disable-line
  const markupByPool = useMemo(() => cascade ? computeMarkupRatesByPool(cascade, mode) : {}, [cascade]); // eslint-disable-line
  // Pools overhead (coûts redistribués) — exclus de la table Capacity Costing par personne.
  const overheadPoolIds = useMemo(
    () => new Set((cascade?.pools || []).filter(p => p.is_overhead_pool).map(p => p.pool_id)),
    [cascade],
  );

  const rows = useMemo(() => {
    const list = (chr?.rows || []).map(r => {
      const lcr = Number(r.lcr || 0);
      const tr  = Number(r.heures_prod || 0);
      const ot  = Number(r.heures_contractuelles || 0);
      const tf  = Number(r.heures_tf || 0);
      const nf  = Number(r.heures_nf || 0);
      const burden = (r.cost_pool_id && burdenByPool[r.cost_pool_id]) || 0;
      const mk = (r.cost_pool_id && markupByPool[r.cost_pool_id]) || null;
      const indPers  = mk ? mk.indirect_personnel : 0;   // mark-up personnel indirect (CHF/h)
      const overhead = mk ? mk.overhead : 0;             // mark-up overhead (CHF/h)
      const pcr = lcr + burden;                           // CCR = LCR + Indirect personnel + Overhead
      const worked = tf + nf;                             // Production hours (heures travaillées)
      const idle = Math.max(0, tr - tf - nf);
      const total = pcr * tr;                             // Capacity cost = CCR × TR
      return {
        ...r,
        name: `${r.first_name} ${(r.last_name || '').toUpperCase()}`,
        isOverheadPerson: overheadPoolIds.has(r.cost_pool_id),
        lcr, indPers, overhead, tr, ot, tf, nf, burden, pcr, worked, idle, total,
        owc_billable: pcr * tf, owc_nonbill: pcr * nf, owc_idle: pcr * idle,
        prodCost: pcr * worked,                           // = Production cost (CCR × Billable+NB)
        bcr: tf > 0 ? total / tf : 0,                     // BCR = Capacity cost ÷ Billable hours
      };
    });
    list.sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''));
    return list;
  }, [chr, burdenByPool, markupByPool, overheadPoolIds]);

  if (!chr || !cascade) {
    return <div className="ppl-print__section"><p>{t('common:status.loading')}</p></div>;
  }

  // Capacity Costing (par personne) : exclut les pools overhead (comme CostAssignmentPage).
  const capRows = rows.filter(r => !r.isOverheadPerson);
  // Production costing : toutes les personnes (comme ChrPage vue pcr).
  const prodRows = rows;

  const sum = (k) => rows.reduce((s, r) => s + (Number(r[k]) || 0), 0);
  const sumOf = (arr, sel) => arr.reduce((s, r) => s + (Number(sel(r)) || 0), 0);
  // Moyenne pondérée LCR (par OT) — utilisée par la section Labour cost.
  const totWork = sum('heures_contractuelles');
  const lcrAvg  = totWork > 0 ? sum('loaded_payroll') / totWork : 0;

  // ── Pied Capacity Costing (capRows : hors pools overhead) ──
  const capOt = sumOf(capRows, r => r.ot);
  const capTr = sumOf(capRows, r => r.tr);
  const capFoot = {
    lcr:      capOt > 0 ? sumOf(capRows, r => r.lcr * r.ot) / capOt : 0,
    indPers:  capTr > 0 ? sumOf(capRows, r => r.indPers * r.tr) / capTr : 0,
    overhead: capTr > 0 ? sumOf(capRows, r => r.overhead * r.tr) / capTr : 0,
    ccr:      capTr > 0 ? sumOf(capRows, r => r.total) / capTr : 0,
    tr:       capTr,
    total:    sumOf(capRows, r => r.total),
  };

  // ── Pied Production costing (prodRows : toutes les personnes) ──
  const prodTf = sumOf(prodRows, r => r.tf);
  const prodFoot = {
    tf:        prodTf,
    nf:        sumOf(prodRows, r => r.nf),
    worked:    sumOf(prodRows, r => r.worked),
    idle:      sumOf(prodRows, r => r.idle),
    tr:        sumOf(prodRows, r => r.tr),
    owcBill:   sumOf(prodRows, r => r.owc_billable),
    owcNonb:   sumOf(prodRows, r => r.owc_nonbill),
    prodCost:  sumOf(prodRows, r => r.prodCost),
    owcIdle:   sumOf(prodRows, r => r.owc_idle),
    capCost:   sumOf(prodRows, r => r.total),
    bcr:       prodTf > 0 ? sumOf(prodRows, r => r.total) / prodTf : 0,
  };

  // ── Cascade (Cost assignment) — allocation par pool selon le mode ──
  const cas = cascade;
  const ct = cas.totals || {};
  const totalTr = ct.production_hours || 0;
  const totalTf = ct.billed_hours || 0;
  const totSpec = ct.specific_indirect || 0;
  const ciResid = Math.max(0, (ct.indirect_costs || 0) - totSpec);
  const alloc = (comp, m, pool, residualTotal) => {
    if (m === 'within') return pool[comp] || 0;
    const tot = residualTotal != null ? residualTotal : (ct[comp] || 0);
    if (m === 'billed') return totalTf > 0 ? (tot / totalTf) * (pool.billed_hours || 0) : 0;
    return totalTr > 0 ? (tot / totalTr) * (pool.production_hours || 0) : 0;
  };
  const casComputed = cas.pools.map(p => {
    const absBrut = alloc('absenteism', mode.absenteism, p);
    const corp    = alloc('corporate', mode.corporate, p);
    const sup     = alloc('supervision', mode.supervision, p);
    const exp     = alloc('expenses', mode.expenses, p);
    const indemRecv = alloc('received_indemnities', mode.absenteism, p);
    const indirectMode = mode.indirect_costs === 'billed' ? 'billed' : 'shared';
    const indirectResid = alloc('indirect_costs', indirectMode, p, ciResid);
    const specific = p.specific_indirect || 0;
    const productive = p.productive_labour || 0;
    const subPers = productive + absBrut + corp + sup;
    const subExt  = exp + (specific + indirectResid) - indemRecv;
    const total   = subPers + subExt;
    return { p, productive, absBrut, corp, sup, exp, specific, indirectResid, indemRecv, subPers, subExt, total };
  });
  const casSum = (sel) => casComputed.reduce((s, c) => s + sel(c), 0);
  // Part indirecte du Total cost assigned (tout sauf Productive labour) + majoration horaire.
  const indirectCostByPool = casComputed.map(c => c.absBrut + c.corp + c.sup + c.subExt);
  const totIndirectCost    = indirectCostByPool.reduce((s, v) => s + v, 0);
  const indBurdenByPool    = casComputed.map((c, i) => (c.p.production_hours > 0 ? indirectCostByPool[i] / c.p.production_hours : 0));
  const indBurdenTotal     = totalTr > 0 ? totIndirectCost / totalTr : 0;

  return (
    <>
      {/* ════════ 1. LABOUR COST ════════ */}
      {show('labour') && (
      <section className="ppl-print__section">
        <PrintReportHeader category="Costing" title="Labour cost" year={year} tenant={activeTenant} editedAt={editedAt} />
        <table className="ppl-print__table ppl-print__table--rnum">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Person</th>
              <th style={{ textAlign: 'left' }}>Department</th>
              <th>FTE %</th><th>Base salary</th><th>Annual extras</th><th>Gross salary</th>
              <th>Social charges employer</th><th>Loaded payroll</th><th>Operational time (TO)</th>
              <th className="ppl-print__col-hl">Labour Cost Rate (LCR)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.person_id}>
                <td style={{ textAlign: 'left' }}>{r.name}</td>
                <td style={{ textAlign: 'left' }}>{r.department || '—'}</td>
                <td>{f0(Number(r.fte_pct))}%</td>
                <td>{f0(r.base_salary)}</td>
                <td>{f0(r.annual_extras)}</td>
                <td>{f0(r.gross_salary)}</td>
                <td>{f0(r.social_charges_empr)}</td>
                <td>{f0(r.loaded_payroll)}</td>
                <td>{f0(r.heures_contractuelles)}</td>
                <td className="ppl-print__col-hl"><strong>{f2(r.lcr)}</strong></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ textAlign: 'left' }}><strong>Total / Average</strong></td>
              <td></td><td></td>
              <td>{f0(sum('base_salary'))}</td>
              <td>{f0(sum('annual_extras'))}</td>
              <td>{f0(sum('gross_salary'))}</td>
              <td>{f0(sum('social_charges_empr'))}</td>
              <td><strong>{f0(sum('loaded_payroll'))}</strong></td>
              <td>{f0(sum('heures_contractuelles'))}</td>
              <td className="ppl-print__col-hl"><strong>{f2(lcrAvg)}</strong></td>
            </tr>
          </tfoot>
        </table>
      </section>
      )}

      {/* ════════ 2. COST ASSIGNMENT — 3 tableaux ════════ */}
      {show('assignment') && (
      <section className="ppl-print__section">
        <PrintReportHeader category="Costing" title="Indirect assignment" year={year} tenant={activeTenant} editedAt={editedAt} />

        {/* Cost drivers sélectionnés (clés d'allocation) — rappel en tête. */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '4mm 8mm', alignItems: 'baseline',
          margin: '0 0 5mm', padding: '2.5mm 3.5mm',
          border: '0.5pt solid #d9d2c6', borderRadius: '4px', fontSize: '8pt',
        }}>
          <span style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#555' }}>Cost drivers</span>
          {[
            ['Absenteism', mode.absenteism],
            ['Corporate', mode.corporate],
            ['Supervision', mode.supervision],
            ['Expenses', mode.expenses],
            ['Indirect costs', mode.indirect_costs === 'billed' ? 'billed' : 'shared'],
          ].map(([label, m]) => (
            <span key={label}>
              <strong>{label}:</strong> {KEY_LABEL[m] || m}
            </span>
          ))}
        </div>

        {/* 2a. Summary — fond distinct */}
        <CasTable title="Summary" pools={cas.pools} bg="#EDE8DF">
          <CasRow label="Operational time (TO)" total={ct.working_hours} vals={cas.pools.map(p => p.working_hours)} fmt={f0} />
          <CasRow label="Practical Capacity (TR)" total={ct.production_hours} vals={cas.pools.map(p => p.production_hours)} fmt={f0} />
          <CasRow label="Billed hours (TF)" total={ct.billed_hours} vals={cas.pools.map(p => p.billed_hours)} fmt={f0} />
          <CasRow label="Personnel charges" total={ct.personnel} vals={cas.pools.map(p => p.personnel)} />
          <CasRow strong label="Total cost assigned" total={casSum(c => c.total)} vals={casComputed.map(c => c.total)} />
          <CasRow label="↳ Out of which indirect cost" total={totIndirectCost} vals={indirectCostByPool} fmt={f0} />
          <CasRow label="↳ Indirect burden rate (CHF/h)" total={indBurdenTotal} vals={indBurdenByPool} fmt={f2} color="var(--color-primary)" />
        </CasTable>

        {/* 2b. Personnel */}
        <CasTable title="Personnel" pools={cas.pools}>
          <CasRow label="Production (LCR × TR)" total={ct.productive_labour} vals={casComputed.map(c => c.productive)} />
          <CasRow label={withKey('Absenteism', mode.absenteism)} total={ct.absenteism} vals={casComputed.map(c => c.absBrut)} />
          <CasRow label={withKey('Corporate', mode.corporate)} total={ct.corporate} vals={casComputed.map(c => c.corp)} />
          <CasRow label={withKey('Supervision', mode.supervision)} total={ct.supervision} vals={casComputed.map(c => c.sup)} />
          <CasRow strong label="Total personnel" total={casSum(c => c.subPers)} vals={casComputed.map(c => c.subPers)} />
        </CasTable>

        {/* 2c. External costs */}
        <CasTable title="External (fees & charges)" pools={cas.pools}>
          <CasRow label={withKey('(–) Received indemnities', mode.absenteism)} total={-(ct.received_indemnities || 0)} vals={casComputed.map(c => -c.indemRecv)} />
          <CasRow label={withKey('Expenses', mode.expenses)} total={ct.expenses} vals={casComputed.map(c => c.exp)} />
          <CasRow label={withKey('Specific indirect costs', 'within')} total={totSpec} vals={cas.pools.map(p => p.specific_indirect)} />
          <CasRow label={withKey('Indirect costs (residual)', mode.indirect_costs === 'billed' ? 'billed' : 'shared')} total={ciResid} vals={casComputed.map(c => c.indirectResid)} />
          <CasRow strong label="Total external cost" total={casSum(c => c.subExt)} vals={casComputed.map(c => c.subExt)} />
        </CasTable>
      </section>
      )}

      {/* ════════ 3. CAPACITY COSTING (CCR par personne, hors pools overhead) ════════ */}
      {show('production') && (
      <section className="ppl-print__section">
        <PrintReportHeader category="Costing" title="Capacity Costing" year={year} tenant={activeTenant} editedAt={editedAt} />
        <table className="ppl-print__table ppl-print__table--rnum ppl-print__table--dense">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Person</th>
              <th style={{ textAlign: 'left' }}>Cost pool</th>
              <th>Labor cost rate (LCR)</th>
              <th>Indirect personnel</th>
              <th>Overhead</th>
              <th className="ppl-print__col-hl">Capacity cost rate (CCR)</th>
              <th>Practical Capacity (TR)</th>
              <th>Capacity Cost</th>
            </tr>
          </thead>
          <tbody>
            {capRows.map(r => (
              <tr key={r.person_id}>
                <td style={{ textAlign: 'left' }} className="nowrap">{r.name}</td>
                <td style={{ textAlign: 'left' }} className="nowrap">{r.cost_pool_name || '—'}</td>
                <td>{f2(r.lcr)}</td>
                <td>{f2(r.indPers)}</td>
                <td>{f2(r.overhead)}</td>
                <td className="ppl-print__col-hl"><strong>{f2(r.pcr)}</strong></td>
                <td>{f0(r.tr)}</td>
                <td><strong>{f0(r.total)}</strong></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ textAlign: 'left' }}><strong>Total / Average</strong></td>
              <td></td>
              <td>{f2(capFoot.lcr)}</td>
              <td>{f2(capFoot.indPers)}</td>
              <td>{f2(capFoot.overhead)}</td>
              <td className="ppl-print__col-hl"><strong>{f2(capFoot.ccr)}</strong></td>
              <td>{f0(capFoot.tr)}</td>
              <td><strong>{f0(capFoot.total)}</strong></td>
            </tr>
          </tfoot>
        </table>
      </section>
      )}

      {/* ════════ 4. PRODUCTION COSTING (heures → coûts @CCR → BCR, toutes personnes) ════════ */}
      {show('pcr') && (
      <section className="ppl-print__section">
        <PrintReportHeader category="Costing" title="Production costing" year={year} tenant={activeTenant} editedAt={editedAt} />
        <table className="ppl-print__table ppl-print__table--rnum ppl-print__table--dense">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Person</th>
              <th style={{ textAlign: 'left' }}>Cost pool</th>
              <th className="ppl-print__col-hl">Billable hours</th>
              <th>Non billable hours</th>
              <th>Production hours</th>
              <th>+ Idle time</th>
              <th>= Capacity hours</th>
              <th>Billable cost</th>
              <th>Non billable cost</th>
              <th>= Production cost</th>
              <th>Idle cost</th>
              <th className="ppl-print__col-hl">= Capacity cost</th>
              <th className="ppl-print__col-hl">Billable cost rate (BCR)</th>
            </tr>
          </thead>
          <tbody>
            {prodRows.map(r => (
              <tr key={r.person_id}>
                <td style={{ textAlign: 'left' }} className="nowrap">{r.name}</td>
                <td style={{ textAlign: 'left' }} className="nowrap">{r.cost_pool_name || '—'}</td>
                <td className="ppl-print__col-hl">{f0(r.tf)}</td>
                <td>{f0(r.nf)}</td>
                <td>{f0(r.worked)}</td>
                <td>{f0(r.idle)}</td>
                <td><strong>{f0(r.tr)}</strong></td>
                <td>{f0(r.owc_billable)}</td>
                <td>{f0(r.owc_nonbill)}</td>
                <td><strong>{f0(r.prodCost)}</strong></td>
                <td>{f0(r.owc_idle)}</td>
                <td className="ppl-print__col-hl"><strong>{f0(r.total)}</strong></td>
                <td className="ppl-print__col-hl"><strong>{f2(r.bcr)}</strong></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ textAlign: 'left' }}><strong>Total / Average</strong></td>
              <td></td>
              <td className="ppl-print__col-hl">{f0(prodFoot.tf)}</td>
              <td>{f0(prodFoot.nf)}</td>
              <td>{f0(prodFoot.worked)}</td>
              <td>{f0(prodFoot.idle)}</td>
              <td><strong>{f0(prodFoot.tr)}</strong></td>
              <td>{f0(prodFoot.owcBill)}</td>
              <td>{f0(prodFoot.owcNonb)}</td>
              <td><strong>{f0(prodFoot.prodCost)}</strong></td>
              <td>{f0(prodFoot.owcIdle)}</td>
              <td className="ppl-print__col-hl"><strong>{f0(prodFoot.capCost)}</strong></td>
              <td className="ppl-print__col-hl"><strong>{f2(prodFoot.bcr)}</strong></td>
            </tr>
          </tfoot>
        </table>
      </section>
      )}
    </>
  );
}

/* Tableau cascade : Composant + TOTAL + 1 colonne par pool. bg = fond optionnel. */
function CasTable({ title, pools, bg, children }) {
  return (
    <table className="ppl-print__table" style={{ marginBottom: 22, tableLayout: 'fixed', ...(bg ? { background: bg } : {}) }}>
      {/* Colgroup commun aux 3 blocs → colonnes parfaitement alignées entre eux. */}
      <colgroup>
        <col style={{ width: '34%' }} />
        <col style={{ width: '11%' }} />
        {pools.map(p => <col key={p.pool_id} />)}
      </colgroup>
      <thead>
        <tr style={bg ? { background: bg } : undefined}>
          <th style={{ textAlign: 'left' }}>{title}</th>
          <th>TOTAL</th>
          {pools.map(p => <th key={p.pool_id}>{p.pool_name}</th>)}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}

function CasRow({ label, total, vals, fmt = f0, strong, color }) {
  const st = color ? { color, fontWeight: 700 } : undefined;
  const wrap = (v) => ((strong || color) ? <strong>{v}</strong> : v);
  return (
    <tr>
      <td style={{ textAlign: 'left', ...(st || {}) }}>{wrap(label)}</td>
      <td style={st}>{wrap(fmt(total))}</td>
      {vals.map((v, i) => <td key={i} style={st}>{wrap(fmt(v))}</td>)}
    </tr>
  );
}
